import React, { useEffect, useState, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import PageHeader from '@/components/system/PageHeader';
import SectionCard from '@/components/system/SectionCard';
import MetricTooltip, { METRIC_EXPLANATIONS } from '@/components/system/MetricTooltip';
import { AppBreadcrumb } from '@/components/system/AppBreadcrumb';
import { ContextBackButton } from '@/components/system/ContextBackButton';
import { CHART_TOOLTIP_STYLE } from '@/components/system/ChartTooltip';
import { InteractiveChart } from '@/components/system/InteractiveChart';
import { InsightList } from '@/components/system/InsightCard';
import { DiagnosticPanel } from '@/components/system/DiagnosticPanel';
import { RecommendationPanel } from '@/components/system/RecommendationPanel';
import { generateInsights } from '@/lib/insightRules';
import { generateDiagnostic } from '@/lib/diagnosticEngine';
import { generateRecommendations } from '@/lib/recommendationEngine';
import type { AnalyticalInsight } from '@/types/insights';
import type { RootCauseAnalysis } from '@/types/diagnostics';
import type { OperationalRecommendation } from '@/types/recommendations';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import {
  TrendingUp, TrendingDown, Minus, Activity, BarChart3, Zap, Lightbulb,
} from 'lucide-react';
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend,
} from 'recharts';
import { format } from 'date-fns';

// ─── Types ──────────────────────────────────────────────────────────
interface TrendRow {
  contract_id: string;
  contrato_nome: string;
  data_referencia: string;
  fonte_dados: string;
  prob_atraso: number | null;
  prob_estouro: number | null;
  p80_duracao: number | null;
  p80_custo: number | null;
  fator_ajuste: number | null;
  delta_prob_atraso: number | null;
  delta_prob_estouro: number | null;
  tendencia_mc: string | null;
}

const TREND_ICON: Record<string, React.ReactNode> = {
  piorando: <TrendingUp className="h-4 w-4 text-destructive" />,
  melhorando: <TrendingDown className="h-4 w-4 text-green-500" />,
  estavel: <Minus className="h-4 w-4 text-muted-foreground" />,
};

const CHART_COLORS = [
  'hsl(var(--primary))',
  'hsl(var(--destructive))',
  'hsl(var(--chart-3, 200 70% 50%))',
  'hsl(var(--chart-4, 45 90% 50%))',
  'hsl(var(--chart-5, 280 65% 60%))',
  'hsl(142 71% 45%)',
  'hsl(30 90% 50%)',
  'hsl(330 70% 50%)',
  'hsl(190 80% 45%)',
];

// ─── Main Component ─────────────────────────────────────────────────
const RiskTrendsDashboard: React.FC = () => {
  const navigate = useNavigate();
  const [data, setData] = useState<TrendRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [activeDiagnostic, setActiveDiagnostic] = useState<RootCauseAnalysis | null>(null);
  const [activeRecommendations, setActiveRecommendations] = useState<OperationalRecommendation[]>([]);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      const { data: rows, error } = await supabase
        .from('vw_risk_trends')
        .select('*')
        .order('data_referencia', { ascending: true });
      if (!error && rows) setData(rows as TrendRow[]);
      setLoading(false);
    };
    fetchData();
  }, []);

  // Group by contract for chart
  const contracts = useMemo(() => {
    const map = new Map<string, string>();
    for (const r of data) map.set(r.contract_id, r.contrato_nome);
    return Array.from(map.entries()).map(([id, nome]) => ({ id, nome }));
  }, [data]);

  // Line chart data — pivot by date
  const chartData = useMemo(() => {
    const dateMap = new Map<string, Record<string, number | string>>();
    for (const r of data) {
      if (r.fonte_dados !== 'monte_carlo') continue;
      const dateKey = format(new Date(r.data_referencia), 'dd/MM HH:mm');
      if (!dateMap.has(dateKey)) dateMap.set(dateKey, { date: dateKey });
      const entry = dateMap.get(dateKey)!;
      entry[r.contract_id] = r.prob_atraso ?? 0;
    }
    return Array.from(dateMap.values());
  }, [data]);

  // Latest state per contract
  const latestByContract = useMemo(() => {
    const map = new Map<string, TrendRow>();
    for (const r of data) {
      if (r.fonte_dados !== 'monte_carlo') continue;
      const existing = map.get(r.contract_id);
      if (!existing || new Date(r.data_referencia) > new Date(existing.data_referencia)) {
        map.set(r.contract_id, r);
      }
    }
    return Array.from(map.values());
  }, [data]);

  // Contracts with growing risk
  const growingRisk = useMemo(() =>
    latestByContract
      .filter(r => (r.delta_prob_atraso ?? 0) > 0)
      .sort((a, b) => (b.delta_prob_atraso ?? 0) - (a.delta_prob_atraso ?? 0)),
  [latestByContract]);

  // Contracts improving
  const improvingRisk = useMemo(() =>
    latestByContract
      .filter(r => (r.delta_prob_atraso ?? 0) < 0)
      .sort((a, b) => (a.delta_prob_atraso ?? 0) - (b.delta_prob_atraso ?? 0)),
  [latestByContract]);

  // Volatility — stddev of prob_atraso per contract
  const volatility = useMemo(() => {
    const groups = new Map<string, number[]>();
    for (const r of data) {
      if (r.fonte_dados !== 'monte_carlo' || r.prob_atraso == null) continue;
      if (!groups.has(r.contract_id)) groups.set(r.contract_id, []);
      groups.get(r.contract_id)!.push(r.prob_atraso);
    }
    const result: Array<{ contract_id: string; nome: string; stddev: number; mean: number; count: number }> = [];
    for (const [cid, values] of groups) {
      if (values.length < 2) continue;
      const mean = values.reduce((s, v) => s + v, 0) / values.length;
      const variance = values.reduce((s, v) => s + (v - mean) ** 2, 0) / values.length;
      const stddev = Math.sqrt(variance);
      const nome = latestByContract.find(r => r.contract_id === cid)?.contrato_nome ?? cid;
      result.push({ contract_id: cid, nome, stddev: Math.round(stddev * 100) / 100, mean: Math.round(mean * 100) / 100, count: values.length });
    }
    return result.sort((a, b) => b.stddev - a.stddev);
  }, [data, latestByContract]);

  // KPIs
  const kpis = useMemo(() => ({
    totalContratos: contracts.length,
    piorando: latestByContract.filter(r => r.tendencia_mc === 'piorando').length,
    melhorando: latestByContract.filter(r => r.tendencia_mc === 'melhorando').length,
    highVolatility: volatility.filter(v => v.stddev > 5).length,
  }), [contracts, latestByContract, volatility]);

  // ── Insights from rule engine
  const trendInsightsContext = useMemo(() => ({
    trends: latestByContract.map(r => ({
      contract_id: r.contract_id,
      contrato_nome: r.contrato_nome,
      variacao: Math.abs(r.delta_prob_atraso ?? 0),
      direcao: r.tendencia_mc === 'piorando' ? 'piorando' as const : r.tendencia_mc === 'melhorando' ? 'melhorando' as const : 'estavel' as const,
    })),
    portfolio: {
      total: contracts.length,
      criticos: 0,
      altos: 0,
      moderados: 0,
      baixos: 0,
      piorando: kpis.piorando,
    },
  }), [latestByContract, contracts, kpis]);

  const trendInsights = useMemo(() => generateInsights(trendInsightsContext), [trendInsightsContext]);

  const trendDiagnosticContext = useMemo(() => ({
    contracts: latestByContract.map(r => ({
      contract_id: r.contract_id,
      contrato_nome: r.contrato_nome,
      score_criticidade: 0,
      nivel_criticidade: '',
      prob_atraso_mc: r.prob_atraso ?? null,
      tendencia: r.tendencia_mc,
      total_ocorrencias: null,
    })),
  }), [latestByContract]);

  const handleInsightClick = useCallback((insight: AnalyticalInsight) => {
    const diag = generateDiagnostic(insight, trendDiagnosticContext);
    setActiveDiagnostic(diag);
    if (diag) {
      setActiveRecommendations(generateRecommendations(insight, diag, trendDiagnosticContext));
    } else {
      setActiveRecommendations([]);
    }
  }, [trendDiagnosticContext]);

  if (loading) {
    return (
      <div className="space-y-6 p-6">
        <Skeleton className="h-10 w-64" />
        <div className="grid grid-cols-4 gap-4">
          {[1,2,3,4].map(i => <Skeleton key={i} className="h-28" />)}
        </div>
        <Skeleton className="h-72" />
      </div>
    );
  }

  return (
    <div className="space-y-6 p-6">
      <AppBreadcrumb items={[
        { label: 'Empreendimentos', href: '/empreendimentos' },
        { label: 'Inteligência de Risco', href: '/empreendimentos/inteligencia' },
        { label: 'Tendências' },
      ]} />
      <ContextBackButton to="/empreendimentos/inteligencia" label="Inteligência de Risco" />
      <PageHeader
        title="Tendência e Evolução de Risco"
        subtitle="Análise temporal de indicadores de risco da carteira"
      />

      {/* KPIs */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <SectionCard title="Contratos Monitorados">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-primary/10"><BarChart3 className="h-5 w-5 text-primary" /></div>
            <div>
              <p className="text-xs text-muted-foreground">Total</p>
              <p className="text-2xl font-bold">{kpis.totalContratos}</p>
            </div>
          </div>
        </SectionCard>

        <SectionCard title="Risco Crescente">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-destructive/10"><TrendingUp className="h-5 w-5 text-destructive" /></div>
            <div>
              <p className="text-xs text-muted-foreground">Piorando</p>
              <p className="text-2xl font-bold text-destructive">{kpis.piorando}</p>
            </div>
          </div>
        </SectionCard>

        <SectionCard title="Em Melhoria">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-emerald-500/10"><TrendingDown className="h-5 w-5 text-emerald-500" /></div>
            <div>
              <p className="text-xs text-muted-foreground">Melhorando</p>
              <p className="text-2xl font-bold text-emerald-600">{kpis.melhorando}</p>
            </div>
          </div>
        </SectionCard>

        <SectionCard title={
          <MetricTooltip explanation={METRIC_EXPLANATIONS.volatilidade}>
            <span>Alta Volatilidade</span>
          </MetricTooltip>
        }>
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-orange-500/10"><Zap className="h-5 w-5 text-orange-500" /></div>
            <div>
              <p className="text-xs text-muted-foreground">Desvio padrão &gt; 5</p>
              <p className="text-2xl font-bold text-orange-500">{kpis.highVolatility}</p>
            </div>
          </div>
        </SectionCard>
      </div>

      {/* Main Trend Chart */}
      <SectionCard title={
        <MetricTooltip explanation={METRIC_EXPLANATIONS.probAtraso}>
          <span>Evolução da Probabilidade de Atraso</span>
        </MetricTooltip>
      }>
        {chartData.length === 0 ? (
          <div className="flex items-center justify-center h-64 text-muted-foreground">
            Dados insuficientes para gráfico temporal (necessita múltiplas execuções)
          </div>
        ) : (
          <InteractiveChart
            data={chartData}
            hint="Clique em um ponto para analisar o contrato"
            height={320}
          >
            {(chartProps) => (
              <LineChart data={chartData}>
                <CartesianGrid strokeDasharray="3 3" className="opacity-30" />
                <XAxis dataKey="date" tick={{ fontSize: 11 }} />
                <YAxis domain={[0, 100]} tick={{ fontSize: 11 }} unit="%" />
                <Tooltip {...chartProps.tooltipProps} />
                <Legend />
                {contracts.map((c, idx) => (
                  <Line
                    key={c.id}
                    type="monotone"
                    dataKey={c.id}
                    name={c.nome.length > 25 ? c.nome.slice(0, 25) + '…' : c.nome}
                    stroke={CHART_COLORS[idx % CHART_COLORS.length]}
                    strokeWidth={2}
                    dot={{ r: 3 }}
                    activeDot={{
                      r: 6,
                      className: 'cursor-pointer',
                      onClick: () => navigate(`/empreendimentos/inteligencia/${c.id}`),
                    }}
                    connectNulls
                  />
                ))}
              </LineChart>
            )}
          </InteractiveChart>
        )}
      </SectionCard>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Growing Risk */}
        <SectionCard title="Contratos com Risco Crescente">
          {growingRisk.length === 0 ? (
            <p className="text-sm text-muted-foreground py-4">Nenhum contrato com aumento de risco detectado</p>
          ) : (
            <div className="space-y-2">
              {growingRisk.map(r => (
                <button
                  key={r.contract_id}
                  onClick={() => navigate(`/empreendimentos/inteligencia/${r.contract_id}`)}
                  className="w-full flex items-center justify-between py-2 px-3 rounded-lg border border-border/50 hover:bg-muted/50 transition-colors cursor-pointer text-left group"
                >
                  <div className="flex items-center gap-2">
                    <TrendingUp className="h-4 w-4 text-destructive" />
                    <span className="text-sm font-medium truncate max-w-[200px]">{r.contrato_nome}</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <span className="text-sm">{r.prob_atraso ?? 0}%</span>
                    <Badge variant="outline" className="bg-destructive/10 text-destructive border-destructive/30">
                      +{r.delta_prob_atraso ?? 0}pp
                    </Badge>
                  </div>
                </button>
              ))}
            </div>
          )}
        </SectionCard>

        {/* Improving */}
        <SectionCard title="Contratos em Melhoria">
          {improvingRisk.length === 0 ? (
            <p className="text-sm text-muted-foreground py-4">Nenhum contrato com redução de risco detectado</p>
          ) : (
            <div className="space-y-2">
              {improvingRisk.map(r => (
                <button
                  key={r.contract_id}
                  onClick={() => navigate(`/empreendimentos/inteligencia/${r.contract_id}`)}
                  className="w-full flex items-center justify-between py-2 px-3 rounded-lg border border-border/50 hover:bg-muted/50 transition-colors cursor-pointer text-left group"
                >
                  <div className="flex items-center gap-2">
                    <TrendingDown className="h-4 w-4 text-green-500" />
                    <span className="text-sm font-medium truncate max-w-[200px]">{r.contrato_nome}</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <span className="text-sm">{r.prob_atraso ?? 0}%</span>
                    <Badge variant="outline" className="bg-green-500/10 text-green-600 border-green-500/30">
                      {r.delta_prob_atraso ?? 0}pp
                    </Badge>
                  </div>
                </button>
              ))}
            </div>
          )}
        </SectionCard>
      </div>

      {/* Volatility */}
      <SectionCard title="Indicador de Volatilidade">
        {volatility.length === 0 ? (
          <p className="text-sm text-muted-foreground py-4">
            Dados insuficientes para cálculo de volatilidade (necessita múltiplas execuções por contrato)
          </p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-border">
                  <th className="text-left py-3 px-4 text-muted-foreground font-medium">Contrato</th>
                  <th className="text-center py-3 px-4 text-muted-foreground font-medium">Desvio Padrão</th>
                  <th className="text-center py-3 px-4 text-muted-foreground font-medium">Média Prob. Atraso</th>
                  <th className="text-center py-3 px-4 text-muted-foreground font-medium">Execuções</th>
                  <th className="text-center py-3 px-4 text-muted-foreground font-medium">Classificação</th>
                </tr>
              </thead>
              <tbody>
                {volatility.map(v => (
                  <tr key={v.contract_id} className="border-b border-border/50 hover:bg-muted/30 cursor-pointer transition-colors" onClick={() => navigate(`/empreendimentos/inteligencia/${v.contract_id}`)}>
                    <td className="py-3 px-4 font-medium truncate max-w-[200px]">{v.nome}</td>
                    <td className="py-3 px-4 text-center font-bold">{v.stddev}</td>
                    <td className="py-3 px-4 text-center">{v.mean}%</td>
                    <td className="py-3 px-4 text-center">{v.count}</td>
                    <td className="py-3 px-4 text-center">
                      <Badge variant="outline" className={
                        v.stddev > 10 ? 'bg-destructive/10 text-destructive border-destructive/30' :
                        v.stddev > 5 ? 'bg-orange-500/10 text-orange-500 border-orange-500/30' :
                        'bg-green-500/10 text-green-600 border-green-500/30'
                      }>
                        {v.stddev > 10 ? 'Alta' : v.stddev > 5 ? 'Média' : 'Baixa'}
                      </Badge>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </SectionCard>

      {/* ── Insights do Sistema ──────────────────────────── */}
      {trendInsights.length > 0 && (
        <SectionCard title="Insights do Sistema" icon={Lightbulb}>
          <InsightList insights={trendInsights} maxItems={4} compact onInsightClick={handleInsightClick} />
        </SectionCard>
      )}

      {/* ── Diagnóstico de Causa Raiz ────────────────────── */}
      {activeDiagnostic && (
        <DiagnosticPanel
          diagnostic={activeDiagnostic}
          onClose={() => { setActiveDiagnostic(null); setActiveRecommendations([]); }}
        />
      )}

      {/* ── Recomendações Operacionais ────────────────────── */}
      {activeRecommendations.length > 0 && (
        <RecommendationPanel
          recommendations={activeRecommendations}
          onClose={() => setActiveRecommendations([])}
        />
      )}
    </div>
  );
};

export default RiskTrendsDashboard;
